import { useEffect } from 'react';

import { brazilianRealFormat } from 'app/utils';
import { ProductTypeEnum, ProductsSidebarButton, ProductsSidebarInterestButton, useProducts } from 'features/products';
import { useSchool } from 'features/school';
import { useResources } from 'features/resources';
import { useTracking } from 'features/tracking';
import { Flex, Typography } from 'components';

interface ProductEnrollmentBoxProps {
  productType?: ProductTypeEnum;
  apiFetch: (forceEnroll: boolean) => void;
  paymentLinkUrl: string | false | null;
  onlyButton?: boolean;
  hideDetails?: boolean;
  hidePriceIfFree?: boolean;
}

const ProductEnrollmentBox = ({
  productType = ProductTypeEnum.Unspecified,
  paymentLinkUrl,
  apiFetch,
  onlyButton = false,
  hideDetails = false,
  hidePriceIfFree = false,
}: ProductEnrollmentBoxProps) => {
  const { productDetails, availableClasses, clearAvailableClasses } = useProducts();
  const {
    schools: { schoolBenndorf, schoolMPC },
  } = useResources();

  const { schoolAlias } = useSchool();

  const { eventTracker } = useTracking();

  useEffect(() => {
    return () => {
      clearAvailableClasses();
    };
  }, []);

  let intentProductsLabel = 'curso';
  if (schoolBenndorf) {
    intentProductsLabel = 'produto';
  } else if (schoolMPC) {
    intentProductsLabel = 'conteúdo';
  }

  const showDetails = onlyButton ? false : hideDetails ? false : true;
  const showPrice = (isFree: boolean) => (onlyButton ? false : hidePriceIfFree && isFree ? false : true);

  const InterestBox = () => (
    <Flex flexDirection="column">
      {showDetails && (
        <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="white" borderTopLeftRadius={4} borderTopRightRadius={4}>
          <Typography as="span" type="type2" lineHeight="1.3" color="gray_500" upper>
            {productDetails?.product.hat}
          </Typography>

          <Typography as="span" type="type7" lineHeight="1.1" fontWeight="bold">
            {productDetails?.product.name}
          </Typography>

          <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
            {productDetails?.product.description}
          </Typography>
        </Flex>
      )}

      <Flex flexDirection="column">
        {!onlyButton && (
          <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
            <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
              Inscrição
            </Typography>

            <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2} upper>
              Matrícula não disponível
            </Typography>

            <Typography as="span" type="type2" lineHeight="1.3" color="white" mt={2}>
              Demonstre seu interesse nesse {intentProductsLabel}.
            </Typography>
          </Flex>
        )}

        <ProductsSidebarInterestButton label={`Tenho interesse nesse ${intentProductsLabel}`} bordered={onlyButton} />
      </Flex>
    </Flex>
  );

  return (
    <>
      {schoolAlias !== 'b3' ? (
        // === DEMAIS ESCOLAS === \\
        <>
          {
            // PRODUCTO POSSUI ENROLLMENT HABILITADO
            availableClasses?.current?.allowEnrollments ? (
              // PRODUTO É GRATUITO
              !availableClasses.current.installmentValue ? (
                // PRODUTO PERMITE ENROLL SOB-DEMANDA
                productDetails?.allowOnDemandEnrollments ? (
                  <Flex flexDirection="column">
                    {showDetails && (
                      <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="white" borderTopLeftRadius={4} borderTopRightRadius={4}>
                        <Typography as="span" type="type2" lineHeight="1.3" color="gray_500" upper>
                          {productDetails?.product.hat}
                        </Typography>

                        <Typography as="span" type="type7" lineHeight="1.1" fontWeight="bold">
                          {productDetails?.product.name}
                        </Typography>

                        <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                          {productDetails?.product.description}
                        </Typography>
                      </Flex>
                    )}

                    <Flex flexDirection="column">
                      {showPrice(true) && (
                        <Flex
                          p={{ m: 4, d: 5 }}
                          flexDirection="column"
                          bg="dark_350"
                          borderTopLeftRadius={showDetails ? 0 : 4}
                          borderTopRightRadius={showDetails ? 0 : 4}
                        >
                          <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                            {productType === ProductTypeEnum.Course && 'Curso'}
                            {productType === ProductTypeEnum.Journey && 'Jornada'}
                            {productType === ProductTypeEnum.MasterClass && 'MasterClass'}
                            {productType === ProductTypeEnum.Program && 'Programa'}
                            {productType === ProductTypeEnum.Book && 'Livro'}
                          </Typography>

                          <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2} upper>
                            Gratuito
                          </Typography>
                        </Flex>
                      )}

                      <ProductsSidebarButton
                        onClick={() => apiFetch(true)}
                        label="Matricular agora"
                        type="nolink"
                        to=""
                        borderRadiusTop={!showDetails || !showPrice(true)}
                      />
                    </Flex>
                  </Flex>
                ) : paymentLinkUrl ? (
                  // PRODUTO NAO PERMITE ENROLL SOB-DEMANDA
                  // PRODUTO POSSUI LINK DE CHECKOUT
                  <Flex flexDirection="column">
                    {showDetails && (
                      <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="white" borderTopLeftRadius={4} borderTopRightRadius={4}>
                        <Typography as="span" type="type2" lineHeight="1.3" color="gray_500" upper>
                          {productDetails?.product.hat}
                        </Typography>

                        <Typography as="span" type="type7" lineHeight="1.1" fontWeight="bold">
                          {productDetails?.product.name}
                        </Typography>

                        <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                          {productDetails?.product.description}
                        </Typography>
                      </Flex>
                    )}

                    <Flex flexDirection="column">
                      {showPrice(true) && (
                        <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
                          <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                            Curso
                          </Typography>

                          <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2} upper>
                            Gratuito
                          </Typography>
                        </Flex>
                      )}

                      <ProductsSidebarButton
                        onClick={() => {
                          if (schoolMPC) {
                            eventTracker({ category: 'checkout', action: 'Add to Cart', label: productDetails?.product?.name });
                          }
                        }}
                        label="Matricular agora"
                        type="external"
                        to={paymentLinkUrl}
                        target="_blank"
                        borderRadiusTop={!showDetails || !showPrice(true)}
                      />
                    </Flex>
                  </Flex>
                ) : (
                  // PRODUTO NAO PERMITE ENROLL SOB-DEMANDA
                  // PRODUTO NÃO POSSUI LINK DE CHECKOUT
                  // MOSTRA BOX DE INTERESSE
                  <InterestBox />
                )
              ) : (
                // PRODUTO NÃO É GRATUITO
                // PRODUTO POSSUI LINK DE CARRINHO
                <>
                  {paymentLinkUrl ? (
                    <Flex flexDirection="column">
                      {showDetails && (
                        <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="white" borderTopLeftRadius={4} borderTopRightRadius={4}>
                          <Typography as="span" type="type2" lineHeight="1.3" color="gray_500" upper>
                            {productDetails?.product.hat}
                          </Typography>

                          <Typography as="span" type="type7" lineHeight="1.1" fontWeight="bold">
                            {productDetails?.product.name}
                          </Typography>

                          <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                            {productDetails?.product.description}
                          </Typography>
                        </Flex>
                      )}

                      <Flex flexDirection="column">
                        {!onlyButton && (
                          <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
                            <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                              Compre
                            </Typography>

                            <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2}>
                              {availableClasses.current.installment}x {brazilianRealFormat(availableClasses.current.installmentValue)}
                            </Typography>

                            <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                              ou {brazilianRealFormat(availableClasses.current.price)} a vista
                            </Typography>
                          </Flex>
                        )}

                        <ProductsSidebarButton
                          onClick={() => {
                            if (schoolMPC) {
                              eventTracker({ category: 'checkout', action: 'Add to Cart', label: productDetails?.product?.name });
                            }
                          }}
                          label="Comprar agora"
                          type="external"
                          to={paymentLinkUrl}
                          target="_blank"
                          borderRadiusTop={onlyButton}
                        />
                      </Flex>
                    </Flex>
                  ) : (
                    // PRODUTO NÃO É GRATUITO
                    // PRODUTO NAO POSSUI LINK DE CARRINHO
                    // MOSTRA BOX DE INTERESSE
                    <InterestBox />
                  )}
                </>
              )
            ) : (
              // PRODUCTO NÃO POSSUI ENROLLMENT HABILITADO
              // MOSTRA BOX DE INTERESSE
              <InterestBox />
            )
          }
        </>
      ) : (
        // === B3 === \\
        <>
          {availableClasses?.current?.allowEnrollments && productDetails?.allowOnDemandEnrollments ? (
            <Flex flexDirection="column">
              {showDetails && (
                <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="white" borderTopLeftRadius={4} borderTopRightRadius={4}>
                  <Typography as="span" type="type2" lineHeight="1.3" color="gray_500" upper>
                    {productDetails?.product.hat}
                  </Typography>

                  <Typography as="span" type="type7" lineHeight="1.1" fontWeight="bold">
                    {productDetails?.product.name}
                  </Typography>

                  <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                    {productDetails?.product.description}
                  </Typography>
                </Flex>
              )}

              <Flex flexDirection="column">
                {showPrice(true) && (
                  <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
                    <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                      Curso
                    </Typography>

                    <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2} upper>
                      Gratuito
                    </Typography>
                  </Flex>
                )}

                <ProductsSidebarButton
                  onClick={() => apiFetch(true)}
                  label="Matricular agora"
                  type="nolink"
                  to=""
                  borderRadiusTop={!showDetails || !showPrice(true)}
                />
              </Flex>
            </Flex>
          ) : (
            <Flex flexDirection="column">
              {!onlyButton && (
                <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="white" borderTopLeftRadius={4} borderTopRightRadius={4}>
                  <Typography as="span" type="type2" lineHeight="1.3" color="gray_500" upper>
                    {productDetails?.product.hat}
                  </Typography>

                  <Typography as="span" type="type7" lineHeight="1.1" fontWeight="bold">
                    {productDetails?.product.name}
                  </Typography>

                  <Typography as="span" type="type1" lineHeight="1.3" color="gray_500" mt={2}>
                    {productDetails?.product.description}
                  </Typography>
                </Flex>
              )}

              <Flex flexDirection="column">
                {!onlyButton && (
                  <Flex p={{ m: 4, d: 5 }} flexDirection="column" bg="dark_350">
                    <Typography as="span" type="type2" lineHeight="1.3" color="white25" upper>
                      Inscrição
                    </Typography>

                    <Typography as="span" type="type5" lineHeight="1.1" color="white" fontWeight="bold" mt={2} upper>
                      Matrícula não disponível
                    </Typography>

                    <Typography as="span" type="type2" lineHeight="1.3" color="white" mt={2}>
                      Demonstre seu interesse nesse {intentProductsLabel}.
                    </Typography>
                  </Flex>
                )}

                <ProductsSidebarInterestButton label={`Tenho interesse nesse ${intentProductsLabel}`} bordered={onlyButton} />
              </Flex>
            </Flex>
          )}
        </>
      )}
    </>
  );
};

export default ProductEnrollmentBox;
